import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Flex, Icon, Text } from '@kognitos/lattice';

/**
 * Status values emitted by Claude's TodoWrite tool.
 */
export type TQuillTodoStatus =
  | 'pending'
  | 'in_progress'
  | 'completed'
  | 'cancelled';

/**
 * Todo item emitted in a Quill todo_progress artifact.
 */
export interface IQuillTodoProgressItem {
  /** Stable todo identifier from the agent. */
  id: string;
  /** User-facing task description. */
  content: string;
  /** Current task status. */
  status: TQuillTodoStatus;
}

/**
 * Props for the Quill todo progress widget.
 */
export interface IQuillTodoProgressWidgetProps {
  /** Todos to display in their emitted order. */
  todos: IQuillTodoProgressItem[];
}

/**
 * Returns the translation key for a TodoWrite status.
 *
 * @param status - TodoWrite status value
 * @returns Translation key for the status label
 */
function getStatusLabelKey(status: TQuillTodoStatus): string {
  return `automation.quillChat.todo.status.${status}`;
}

/**
 * Returns whether the status should be treated as done.
 *
 * @param status - TodoWrite status value
 * @returns Whether the item is completed or cancelled
 */
function isFinishedStatus(status: TQuillTodoStatus): boolean {
  return status === 'completed' || status === 'cancelled';
}

/**
 * Returns whether the item is currently active.
 *
 * @param status - TodoWrite status value
 * @returns Whether the item is in progress
 */
function isActiveStatus(status: TQuillTodoStatus): boolean {
  return status === 'in_progress';
}

/**
 * Renders the current agent todo list inside the Quill chat stream.
 *
 * @param props - Widget props
 * @returns Todo progress widget
 */
export function QuillTodoProgressWidget({
  todos,
}: IQuillTodoProgressWidgetProps) {
  const { t } = useTranslation('automation');
  const [isExpanded, setIsExpanded] = useState(true);
  const listId = useId();
  const completedCount = todos.filter(
    todo => todo.status === 'completed',
  ).length;
  const totalCount = todos.length;

  return (
    <Flex
      direction="column"
      className="min-w-0 rounded-sm border border-border bg-muted px-3 py-2"
      data-testid="quill-todo-progress-widget"
    >
      <Button
        type="button"
        variant="ghost"
        className="h-auto w-full justify-between p-0 !bg-transparent hover:!bg-transparent hover:!text-foreground focus-visible:!bg-transparent active:!bg-transparent"
        aria-expanded={isExpanded}
        aria-controls={listId}
        onClick={() => setIsExpanded(current => !current)}
      >
        <Text level="base" weight="normal" className="truncate">
          {t('automation.quillChat.todo.title')}
        </Text>
        <Flex align="center" gap={2} className="ml-auto">
          <Text level="base" color="muted" weight="normal" className="truncate">
            {t('automation.quillChat.todo.count', {
              completed: completedCount,
              total: totalCount,
            })}
          </Text>
          <Icon
            type={isExpanded ? 'ChevronUp' : 'ChevronDown'}
            className="size-4 text-muted-foreground"
          />
        </Flex>
      </Button>
      {isExpanded && (
        <Flex
          id={listId}
          direction="column"
          gap={3}
          className="mt-4 max-h-64 overflow-y-auto pr-1"
        >
          {todos.map(todo => (
            <Flex key={todo.id} align="center" gap={3} className="min-w-0">
              {isFinishedStatus(todo.status) ? (
                <Icon
                  type="CircleCheck"
                  className="size-5 shrink-0 text-success"
                />
              ) : isActiveStatus(todo.status) ? (
                <Flex
                  align="center"
                  justify="center"
                  className="size-5 shrink-0 rounded-full border border-muted-foreground"
                >
                  <Icon
                    type="ArrowRight"
                    className="size-3 text-muted-foreground"
                  />
                </Flex>
              ) : (
                <Icon
                  type="Circle"
                  className="size-5 shrink-0 text-muted-foreground"
                />
              )}
              <Text
                level="xSmall"
                weight="normal"
                color="muted"
                className={
                  isFinishedStatus(todo.status) ? 'line-through' : undefined
                }
                title={t(getStatusLabelKey(todo.status))}
              >
                {todo.content}
              </Text>
            </Flex>
          ))}
        </Flex>
      )}
    </Flex>
  );
}
